/**
 * Окно «Спроси SKOKA» — короткий диалог о деньгах.
 *
 * Три зоны: шапка с меню, переписка, поле ввода. Переписка — главное: история
 * и служебные действия убраны в меню и не стоят между ответом и вводом.
 *
 * Ответ на финансовый вопрос собран всегда одинаково: вывод, что проверяли,
 * до трёх последствий, следующий шаг и «как посчитано» под раскрытием. Так
 * вывод виден сразу, а формулы не мешают его прочитать.
 *
 * Разговор ничего не меняет в данных. Изменение — только по кнопке в
 * карточке «Что изменится», и только пока расчёт не устарел.
 *
 * Компонент только рисует: что показать и что делать по нажатию, решает
 * вызывающий — настоящий советник или прототип.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { SheetWrap } from '../sheet.js';
import { tidy } from './chat-text.js';
import { Icon } from '../icon.js';
import { Tip } from '../tip.js';

/** Ответ по сценарию. Короткий ответ — обычное сообщение `text`. */
export interface ChatAnswer {
  /** Вывод — одно-два предложения. */
  verdict: string;
  /** Тон вывода: значок рядом с ним, а не только цвет. */
  tone: 'yes' | 'careful' | 'no' | 'info';
  /** Что проверяли: сумма, дата, способ оплаты, период. */
  conditions?: { label: string; value: string }[];
  /** Почему расчёт шире вопроса: например, платёж по карте позже периода. */
  conditionsNote?: string;
  /** До трёх главных последствий. */
  figures?: { label: string; value: string; note?: string; tone?: 'ok' | 'warn' | 'bad' }[];
  /** Ограничение, способное изменить вывод: видно сразу, не под раскрытием. */
  warning?: string;
  /** Следующий шаг — конкретное действие. */
  step?: string;
  /** Как посчитано: подробности и допущения. */
  details?: string[];
}

/** Карточка «Что изменится» — к конкретному ответу. */
export interface PlanChange {
  /** Предложение одной строкой. */
  what: string;
  /**
   * Сейчас → после. `worse` — стало хуже: значок и слово, не только цвет.
   * Без `before` — только следствие: «срок сдвинется на июнь».
   */
  rows: { label: string; before?: string; after: string; worse?: boolean }[];
  /** Пояснение расчёта: на чём он стоит и чего не учитывает. */
  note?: string;
  /** Точное название подтверждения: «Добавить в план», «Применить к плану». */
  confirm: string;
  state: 'offer' | 'applied' | 'stale';
  /** Когда применено: «09:20». */
  appliedAt?: string;
  /** Что изменилось в данных после расчёта. */
  staleReason?: string;
}

export type ChatMsg =
  | { id: string; who: 'you'; text: string; at: string; failed?: boolean }
  | { id: string; who: 'skoka'; kind: 'text'; text: string; at: string }
  | { id: string; who: 'skoka'; kind: 'clarify'; text: string; at: string; replies?: string[] }
  | {
    id: string; who: 'skoka'; kind: 'answer'; answer: ChatAnswer; at: string;
    /** Действия ответа о покупке. Показываются только у последнего ответа. */
    actions?: { plan?: boolean; edit?: boolean; record?: boolean };
    plan?: PlanChange;
  };

/** Доступ к вопросам: свободно, мало осталось, закрыто. */
export type ChatAccess =
  | { kind: 'open'; left?: string }
  | { kind: 'low'; text: string }
  | { kind: 'locked'; title: string; text: string; cta?: string };

export interface PastTalk { id: string; date: string; topic: string; summary: string }

export interface ChatHandlers {
  onSend: (text: string) => void;
  onRetry: (id: string) => void;
  onEditQuestion: (id: string) => void;
  onEditConditions: (id: string) => void;
  onPlan: (id: string) => void;
  onConfirmPlan: (id: string) => void;
  onLaterPlan: (id: string) => void;
  onRecalc: (id: string) => void;
  onRecord: (id: string) => void;
  onNew: () => void;
  onClear: () => void;
  onDocs: () => void;
  onOpenPast: (id: string) => void;
  /** Вопрос из прошлого разговора без ответа — в поле нового. Отправляет человек. */
  onAskAgain?: (text: string) => void;
  /** «Разобрать финансовую картину» — первая строка пустого разговора. */
  onReview?: () => void;
  onAccessCta: () => void;
  onClose: () => void;
}

export interface ChatProps {
  today: string;
  messages: ChatMsg[];
  busy: boolean;
  draft: string;
  setDraft: (s: string) => void;
  access: ChatAccess;
  past: PastTalk[];
  /** Открыт прошлый разговор: только чтение. */
  reading?: { date: string; onBack: () => void };
  /** Последняя реплика SKOKA — уточнение: поле ждёт ответа на него. */
  answering?: boolean;
  examples: string[];
  /** Когда был прошлый разбор — подпись под «Разобрать финансовую картину». */
  lastReview?: string;
  on: ChatHandlers;
}

/** Время сообщения: сегодня — «09:13», раньше — «28 сентября, 18:42». */
function stamp(at: string, today: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  const day = at.slice(0, 10);
  if (day === today) return time;
  return `${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}, ${time}`;
}

export function ChatSheet(p: ChatProps) {
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [menu, setMenu] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  if (view === 'history') {
    return (
      <SheetWrap title="История разборов" onClose={p.on.onClose} onBack={() => setView('chat')} backLabel="Назад к разговору" className="chat">
        <div className="chat-scroll">
          {p.past.length === 0 && <p className="chat-empty">Прошлых разговоров пока нет.</p>}
          {p.past.map((t) => (
            <button key={t.id} className="chat-past" onClick={() => { setView('chat'); p.on.onOpenPast(t.id); }}>
              <span className="d">{t.date}</span>
              <b>{t.topic}</b>
              <span className="s">{t.summary}</span>
            </button>
          ))}
        </div>
      </SheetWrap>
    );
  }

  return (
    <SheetWrap
      title={p.reading ? `Разговор ${p.reading.date}` : 'Спроси SKOKA'}
      onClose={p.on.onClose}
      {...(p.reading ? { onBack: () => { p.reading?.onBack(); setView('history'); }, backLabel: 'Назад к истории' } : {})}
      className="chat"
      action={p.reading ? undefined : (
        <button className="icobtn chat-more" aria-label="Ещё" aria-expanded={menu}
          onClick={() => setMenu((m) => !m)}><MoreIcon /></button>
      )}>
      {menu && (
        <>
          <div className="chat-menu-bg" onClick={() => setMenu(false)} />
          <div className="chat-menu" role="menu">
            {p.access.kind === 'open' && p.access.left && <div className="chat-menu-info">{p.access.left}</div>}
            <button role="menuitem" onClick={() => { setMenu(false); p.on.onNew(); }}>Новый разговор</button>
            <button role="menuitem" onClick={() => { setMenu(false); setView('history'); }}>История разборов</button>
            <button role="menuitem" onClick={() => { setMenu(false); p.on.onDocs(); }}>О расчётах и ограничениях</button>
            {(p.past.length > 0 || p.messages.length > 0) && (
              <button role="menuitem" className="danger" onClick={() => { setMenu(false); setConfirmClear(true); }}>
                Очистить историю
              </button>
            )}
          </div>
        </>
      )}
      {confirmClear && (
        <div className="chat-confirm-bg">
          <div className="chat-confirm" role="alertdialog" aria-labelledby="chat-clear-t">
            <b id="chat-clear-t">Очистить историю?</b>
            <p>Удалятся все разговоры и разборы. Операции, счета и план останутся как есть.</p>
            <div className="chat-confirm-btns">
              <button className="btn" onClick={() => setConfirmClear(false)}>Отмена</button>
              <button className="btn danger" onClick={() => { setConfirmClear(false); p.on.onClear(); }}>Очистить</button>
            </div>
          </div>
        </div>
      )}
      <Conversation {...p} />
    </SheetWrap>
  );
}

function Conversation(p: ChatProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const [fresh, setFresh] = useState(false);
  const last = p.messages[p.messages.length - 1];
  const lastKey = `${p.messages.length}:${last?.id ?? ''}:${p.busy}`;

  const toBottom = (smooth: boolean) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  // При открытии — к последнему сообщению, без анимации.
  useLayoutEffect(() => { toBottom(false); }, []);

  /*
    Новое сообщение не уводит того, кто читает старое.

    Своё сообщение — всегда вниз: человек только что нажал «Отправить» и
    ждёт ответа там. Ответ SKOKA — вниз, только если человек и так внизу;
    иначе появляется «Новый ответ ↓», и он спускается сам, когда дочитает.
  */
  useEffect(() => {
    if (!last) return;
    if (last.who === 'you' || atBottom.current) { toBottom(true); setFresh(false); return; }
    if (!p.busy) setFresh(true);
  }, [lastKey]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 64;
    if (atBottom.current) setFresh(false);
  };

  const lastAnswer = [...p.messages].reverse().find((m) => m.who === 'skoka' && m.kind === 'answer');
  const readOnly = Boolean(p.reading);

  return (
    <>
      <div className="chat-scroll" ref={scroller} onScroll={onScroll}>
        {p.messages.length === 0 && !p.busy && (
          <div className="chat-hello">
            <p>Спроси о своих деньгах — отвечу по твоим счетам, платежам и планам.
              Разговор ничего не меняет, пока ты сам не подтвердишь.</p>
            {/* Разбор картины — первым: кнопка ИИ ведёт и к вопросу, и к разбору. */}
            {p.access.kind !== 'locked' && p.on.onReview && (
              <button className="chat-start" onClick={p.on.onReview}>
                <span className="catico c-violet s-s" aria-hidden="true"><Icon name="sparkles" size={15} /></span>
                <span className="t"><b>Разобрать финансовую картину</b>
                  <small>найду главное и риски{p.lastReview ? ` · прошлый разбор ${p.lastReview}` : ''}</small></span>
                <ArrowIcon />
              </button>
            )}
            {p.access.kind !== 'locked' && p.examples.map((q) => (
              <button key={q} className="chat-example" onClick={() => p.on.onSend(q)}>
                <span>{tidy(q, p.today)}</span><ArrowIcon />
              </button>
            ))}
          </div>
        )}

        {p.messages.map((m, i) => {
          const isLast = i === p.messages.length - 1;
          if (m.who === 'you') {
            return (
              <div key={m.id} className="chat-row you">
                <div className="chat-you">{tidy(m.text, p.today, { dates: false })}</div>
                {m.failed && (
                  <div className="chat-fail" role="alert">
                    <p><WarnIcon /> Не удалось получить ответ. Твой вопрос сохранён.</p>
                    {!p.busy && !readOnly && (
                      <div className="chat-fail-btns">
                        <button className="btn inline" onClick={() => p.on.onRetry(m.id)}>Повторить</button>
                        <button className="btn inline ghost" onClick={() => p.on.onEditQuestion(m.id)}>Изменить вопрос</button>
                      </div>
                    )}
                    {readOnly && p.on.onAskAgain && (
                      <div className="chat-fail-btns">
                        <button className="btn inline" onClick={() => p.on.onAskAgain?.(m.text)}>Спросить снова</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          }
          if (m.kind === 'text') {
            return (
              <div key={m.id} className="chat-row skoka">
                <p className="chat-text">{tidy(m.text, p.today)}</p>
                <Stamp at={m.at} today={p.today} />
              </div>
            );
          }
          if (m.kind === 'clarify') {
            return (
              <div key={m.id} className="chat-row skoka">
                <p className="chat-text">{tidy(m.text, p.today)}</p>
                {isLast && !p.busy && !readOnly && m.replies && (
                  <div className="chat-replies">
                    {m.replies.map((r) => (
                      <button key={r} className="chip" onClick={() => p.on.onSend(r)}>{r}</button>
                    ))}
                  </div>
                )}
                <Stamp at={m.at} today={p.today} />
              </div>
            );
          }
          return (
            <Answer key={m.id} msg={m} today={p.today} on={p.on}
              current={!readOnly && m === lastAnswer && !p.busy} />
          );
        })}

        {p.busy && (
          <div className="chat-row skoka">
            <p className="chat-wait" role="status"><span className="dots"><i /><i /><i /></span> Готовлю ответ</p>
          </div>
        )}

        {fresh && (
          <button className="chat-fresh" onClick={() => { toBottom(true); setFresh(false); }}>
            Новый ответ ↓
          </button>
        )}
      </div>

      {!readOnly && <Composer {...p} />}
    </>
  );
}

const Stamp = ({ at, today }: { at: string; today: string }) => {
  const s = stamp(at, today);
  return s ? <span className="chat-stamp">{s}</span> : null;
};

/**
 * Ответ по частям: вывод, что проверили, последствия, важное, шаг, как
 * посчитано. Без действий — их добавляет окно; онбординг рисует то же самое.
 */
export function AnswerBody({ answer: a, today }: { answer: ChatAnswer; today: string }) {
  const t = (s: string) => tidy(s, today);
  return (
    <>
        <p className={`chat-verdict t-${a.tone}`}><ToneIcon tone={a.tone} /><span>{t(a.verdict)}</span></p>

        {a.conditions && a.conditions.length > 0 && (
          <div className="chat-cond">
            <span className="cap">Что проверили</span>
            {a.conditions.map((c) => (
              <div className="r" key={c.label}><span>{c.label}</span><b>{t(c.value)}</b></div>
            ))}
            {a.conditionsNote && <p className="cnote">{t(a.conditionsNote)}</p>}
          </div>
        )}

        {a.figures && a.figures.length > 0 && (
          <div className="chat-figs">
            {a.figures.slice(0, 3).map((f) => (
              <div className="f" key={f.label}>
                <div className="l"><span>{t(f.label)}</span>{f.note && <i>{t(f.note)}</i>}</div>
                <b className={f.tone ? `t-${f.tone}` : undefined}>{t(f.value)}</b>
              </div>
            ))}
          </div>
        )}

        {a.warning && (
          <p className="chat-warn"><WarnIcon /><span><b>Важно.</b> {t(a.warning)}</span></p>
        )}

        {a.step && (
          <div className="chat-step"><span className="cap">Следующий шаг</span><p>{t(a.step)}</p></div>
        )}

        {a.details && a.details.length > 0 && (
          <details className="chat-how">
            <summary>Как посчитано <ChevronIcon /></summary>
            <ul>{a.details.map((d) => <li key={d}>{t(d)}</li>)}</ul>
          </details>
        )}
    </>
  );
}

function Answer({ msg, today, on, current }: {
  msg: Extract<ChatMsg, { kind: 'answer' }>; today: string; on: ChatHandlers; current: boolean;
}) {
  // Пока к ответу открыта карточка «Что изменится», действия — в ней.
  const showActions = current && msg.actions && !msg.plan;
  return (
    <div className="chat-row skoka">
      <div className="chat-ans">
        <AnswerBody answer={msg.answer} today={today} />

        {showActions && (
          <div className="chat-acts">
            <div className="chat-btns">
              {msg.actions!.edit && (
                <button className="btn" onClick={() => on.onEditConditions(msg.id)}>Изменить условия</button>
              )}
              {msg.actions!.plan && !msg.plan && (
                <button className="btn pri" onClick={() => on.onPlan(msg.id)}>Добавить в план</button>
              )}
            </div>
            {msg.actions!.record && (
              <button className="lnk chat-record" onClick={() => on.onRecord(msg.id)}>
                Уже купил? Записать покупку
              </button>
            )}
          </div>
        )}

        {msg.plan && (
          <PlanCard plan={msg.plan} today={today} current={current}
            onConfirm={() => on.onConfirmPlan(msg.id)}
            onLater={() => on.onLaterPlan(msg.id)}
            onRecalc={() => on.onRecalc(msg.id)} />
        )}
      </div>
      <Stamp at={msg.at} today={today} />
    </div>
  );
}

function PlanCard({ plan, today, current, onConfirm, onLater, onRecalc }: {
  plan: PlanChange; today: string; current: boolean;
  onConfirm: () => void; onLater: () => void; onRecalc: () => void;
}) {
  const t = (s: string) => tidy(s, today);
  return (
    <div className={`chat-plan s-${plan.state}`}>
      <div className="h">
        <b>Что изменится
          {plan.state === 'offer' && current && (
            <>{' '}<Tip>Пока не подтвердишь, ничего не меняется.</Tip></>
          )}</b>
        {plan.state === 'applied' && <span className="tag ok"><CheckIcon /> Применено{plan.appliedAt ? ` · ${plan.appliedAt}` : ''}</span>}
        {plan.state === 'stale' && <span className="tag warn"><WarnIcon /> Нужно пересчитать</span>}
      </div>
      <p className="what">{t(plan.what)}</p>
      {plan.rows.map((r) => (
        <div className="r" key={r.label}>
          <span className="l">{t(r.label)}</span>
          <span className="v">
            {r.before !== undefined && <><s>{t(r.before)}</s> → </>}<b>{t(r.after)}</b>
            {r.worse && <em className="worse">↓ хуже</em>}
          </span>
        </div>
      ))}
      {plan.note && <p className="note-s">{t(plan.note)}</p>}
      {/* Отступ над кнопками — как у действий под ответом: подписи над ними больше нет. */}
      {plan.state === 'offer' && current && (
        <div className="chat-acts">
          <div className="chat-btns">
            <button className="btn" onClick={onLater}>Не сейчас</button>
            <button className="btn pri" onClick={onConfirm}>{plan.confirm}</button>
          </div>
        </div>
      )}
      {plan.state === 'stale' && (
        <>
          <p className="note-s">{plan.staleReason ?? 'Данные изменились после расчёта.'} Применять старый расчёт нельзя.</p>
          {current && <button className="btn" onClick={onRecalc}>Пересчитать</button>}
        </>
      )}
    </div>
  );
}

/** Поле растёт до четырёх строк; дальше прокручивается внутри. */
function Composer(p: ChatProps) {
  const field = useRef<HTMLTextAreaElement>(null);
  const fit = () => {
    const el = field.current;
    if (!el) return;
    // Пустое поле — одна строка, даже если подсказка на узком экране длиннее.
    if (!el.value) { el.style.height = ''; return; }
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 114)}px`;
  };
  useLayoutEffect(fit, [p.draft]);

  if (p.access.kind === 'locked') {
    return (
      <div className="chat-locked">
        <b>{p.access.title}</b>
        <p>{p.access.text}</p>
        {p.access.cta && <button className="btn pri" onClick={p.on.onAccessCta}>{p.access.cta}</button>}
      </div>
    );
  }

  const send = () => {
    const q = p.draft.trim();
    if (!q || p.busy) return;
    p.on.onSend(q);
  };
  // Enter отправляет только с клавиатурой компьютера. На телефоне Enter —
  // новая строка, отправка — стрелкой: так же, как в мессенджерах.
  const fine = typeof window !== 'undefined' && window.matchMedia?.('(pointer: fine)').matches;
  const ready = p.draft.trim().length > 0 && !p.busy;

  return (
    <div className="chat-compose">
      {p.access.kind === 'low' && <p className="chat-left">{p.access.text}</p>}
      <div className="box">
        <textarea
          ref={field}
          rows={1}
          value={p.draft}
          placeholder={p.answering ? 'Твой ответ…' : 'Спроси о своих деньгах…'}
          aria-label={p.answering ? 'Ответ на уточнение' : 'Вопрос SKOKA'}
          onChange={(e) => p.setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (fine && e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
          }} />
        <button className={`send${ready ? ' on' : ''}`} disabled={!ready} aria-label="Отправить" onClick={send}>
          <SendIcon />
        </button>
      </div>
    </div>
  );
}

/* Значки — Font Awesome, как во всём приложении; цвет от текста. */
const MoreIcon = () => <Icon name="ellipsis" size={18} />;
const SendIcon = () => <Icon name="arrow-up" size={18} />;
const ArrowIcon = () => <Icon name="chevron-right" size={14} />;
const ChevronIcon = () => <Icon name="chevron-down" size={13} />;
const CheckIcon = () => <Icon name="check" size={12} />;
const WarnIcon = () => <Icon name="triangle-exclamation" size={14} />;
function ToneIcon({ tone }: { tone: ChatAnswer['tone'] }) {
  const name = tone === 'yes' ? 'check' : tone === 'no' ? 'xmark' : tone === 'careful' ? 'exclamation' : 'info';
  return <span className="ti"><Icon name={name} size={12} /></span>;
}
